import { Outlet } from 'react-router-dom';
import { Suspense } from 'react';
import { faSun, faMoon } from '@fortawesome/free-solid-svg-icons';

import LoadingPage from '../../pages/error-pages/LoadingPage.jsx';
import ChunkErrorBoundary from '../../components/ChunkErrorBoundary.jsx';
import { SecondaryButton } from '../../components/button/Button.jsx';
import { useTheme } from '../../styles/theme/themeContext.jsx';

import './DocsStandaloneLayout.css';

/**
 * Chrome-less layout for the Docs window (/help/docs/*).
 *
 * Docs is opened via `window.open()` from the main app so it lives in its own
 * tab and can be tab-docked like a reference. It deliberately skips the
 * DashboardSidebar / DashboardHeader and renders a minimal branded top-bar
 * instead — the GeoPlay mark and wordmark with a "Docs" label, and the theme toggle — giving the docs
 * their full width and a "separate app" feel.
 */
const DocsStandaloneLayout = () => {
    const { theme, toggleTheme } = useTheme();
    const isDark = theme === 'dark';

    return (
        <div className="docs-standalone">
            <header className="docs-standalone-header">
                {/* The app's GeoPlay mark and wordmark (as in the sidebar), with "Docs" beneath. */}
                <div className="docs-standalone-brand">
                    <span className="docs-standalone-brand-mark" aria-hidden="true">G</span>
                    <span className="docs-standalone-brand-words">
                        <span className="docs-standalone-brand-name">
                            <span className="docs-standalone-brand-geo">Geo</span>
                            <span className="docs-standalone-brand-play">Play</span>
                        </span>
                        <span className="docs-standalone-brand-section">Docs</span>
                    </span>
                </div>

                <div className="docs-standalone-header-actions">
                    <SecondaryButton
                        icon={isDark ? faSun : faMoon}
                        onClick={toggleTheme}
                        aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
                        title={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
                    >
                        {isDark ? 'Light' : 'Dark'}
                    </SecondaryButton>
                </div>
            </header>

            <main className="docs-standalone-main">
                <ChunkErrorBoundary>
                    <Suspense fallback={<LoadingPage />}>
                        <Outlet />
                    </Suspense>
                </ChunkErrorBoundary>
            </main>
        </div>
    );
};

export default DocsStandaloneLayout;
